import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Box, HStack, Icon, VStack } from '@chakra-ui/react';
import { LuClock } from 'react-icons/lu';
import { addDays, format } from 'date-fns';
import { CONTEXT_LABELS, MORE_OPTIONS_BASE } from '@/utils/guest-access/constants.ts';
import { type DurationKey, type ExpirationContext } from '@/components/common/access-time-selector/types.ts';
import {
	composeLocalDate,
	computeExpiryDate,
	formatExpirySummary,
	getExpiryDisplay,
	inferDurationKey,
} from '@/components/common/access-time-selector/utils.ts';
import DurationPills from '@/components/common/access-time-selector/DurationPills.tsx';
import CustomDateTimePicker from '@/components/common/access-time-selector/CustomDateTimePicker.tsx';
import IndefiniteConfirmation from '@/components/common/access-time-selector/IndefiniteConfirmation.tsx';
import ExpirySummaryBar from '@/components/common/access-time-selector/ExpirySummaryBar.tsx';
import { revealStyles } from '@/components/common/access-time-selector/styles.ts';
import { Title } from '@/components/ui/title';

interface ExpirationDatePickerProps {
	expiresAt: Date | null;
	onChange: (date: Date | null) => void;
	allowIndefinite?: boolean;
	showPresets?: boolean;
	context?: ExpirationContext;
	onValidationChange?: (isValid: boolean) => void;
	/**
	 * 'create' (default): indefinite always requires an explicit confirm toggle before it commits.
	 * 'edit': indefinite is gated only when newly selected from a finite state; if it's already
	 * the committed value, the confirmation UI is hidden and a status summary is shown instead.
	 */
	mode?: 'create' | 'edit';
}

export interface ExpirationDatePickerHandle {
	/** Put the picker into pending-indefinite state (gate visible, switch off, not yet committed). */
	requestIndefinite: () => void;
	/** Withdraw a pending `requestIndefinite`, back to the duration `expiresAt` reads as. No-op otherwise. */
	cancelIndefiniteRequest: () => void;
}

type DetailPanel = 'custom' | 'indefinite';

const INDEFINITE_CONFIRMATION_VISUAL_HOLD_MS = 1200;

const ExpirationDatePicker = forwardRef<ExpirationDatePickerHandle, ExpirationDatePickerProps>(
	(
		{
			expiresAt,
			onChange,
			allowIndefinite = true,
			showPresets = true,
			context = 'grant',
			onValidationChange,
			mode = 'create',
		},
		ref,
	) => {
		const labels = CONTEXT_LABELS[context];

		const [timeWindow, setTimeWindow] = useState<DurationKey>(() => inferDurationKey(expiresAt, allowIndefinite));
		const [customDate, setCustomDate] = useState(() =>
			expiresAt ? format(expiresAt, 'yyyy-MM-dd') : format(addDays(new Date(), 1), 'yyyy-MM-dd'),
		);
		const [customTime, setCustomTime] = useState(() => (expiresAt ? format(expiresAt, 'HH:mm') : '12:00'));
		const [customModeLocked, setCustomModeLocked] = useState(
			() => inferDurationKey(expiresAt, allowIndefinite) === 'custom',
		);
		const [indefiniteConfirmed, setIndefiniteConfirmed] = useState(() => expiresAt === null && allowIndefinite);
		const [renderedDetail, setRenderedDetail] = useState<DetailPanel | null>(() =>
			inferDurationKey(expiresAt, allowIndefinite) === 'custom'
				? 'custom'
				: expiresAt !== null && inferDurationKey(expiresAt, allowIndefinite) === 'indefinite'
					? 'indefinite'
					: null,
		);
		const [pendingDetail, setPendingDetail] = useState<DetailPanel | null>(null);
		const [detailPanelOpen, setDetailPanelOpen] = useState(() => renderedDetail !== null);
		const [detailPanelSettled, setDetailPanelSettled] = useState(() => renderedDetail !== null);
		const [indefiniteVisualHold, setIndefiniteVisualHold] = useState(false);
		const lastFiniteExpiryRef = useRef<Date | null>(expiresAt);
		const pendingIndefiniteSelectionRef = useRef(false);
		const indefiniteVisualHoldTimeoutRef = useRef<number | null>(null);
		const detailOpenTimeoutRef = useRef<number | null>(null);

		const clearIndefiniteVisualHoldTimeout = () => {
			if (indefiniteVisualHoldTimeoutRef.current === null) return;
			window.clearTimeout(indefiniteVisualHoldTimeoutRef.current);
			indefiniteVisualHoldTimeoutRef.current = null;
		};

		const moreOptions = useMemo(
			() =>
				allowIndefinite
					? [...MORE_OPTIONS_BASE, { value: 'indefinite' as const, label: labels.indefiniteDropdownLabel }]
					: MORE_OPTIONS_BASE,
			[allowIndefinite, labels.indefiniteDropdownLabel],
		);

		useEffect(() => {
			if (expiresAt) {
				lastFiniteExpiryRef.current = expiresAt;
			}
		}, [expiresAt]);

		useEffect(() => {
			const nextKey = inferDurationKey(expiresAt, allowIndefinite);
			if (expiresAt === null && allowIndefinite) {
				pendingIndefiniteSelectionRef.current = false;
				setCustomModeLocked(false);
				setTimeWindow('indefinite');
				setIndefiniteConfirmed(true);
				return;
			}

			if (pendingIndefiniteSelectionRef.current) {
				return;
			}

			if (expiresAt) {
				setCustomDate(format(expiresAt, 'yyyy-MM-dd'));
				setCustomTime(format(expiresAt, 'HH:mm'));
			}

			if (!customModeLocked) {
				setTimeWindow(nextKey);
			}
		}, [expiresAt, allowIndefinite, customModeLocked]);

		const showCustom = timeWindow === 'custom';
		const showIndefinite = timeWindow === 'indefinite';
		const isIndefinitePending = showIndefinite && expiresAt !== null;
		const isIndefiniteCommitted = showIndefinite && expiresAt === null;
		const showIndefiniteConfirmation = isIndefinitePending || indefiniteVisualHold;
		const desiredDetail: DetailPanel | null = showCustom
			? 'custom'
			: showIndefiniteConfirmation
				? 'indefinite'
				: null;
		const isValid = showIndefinite ? indefiniteConfirmed || isIndefiniteCommitted : !!expiresAt;

		useEffect(() => {
			onValidationChange?.(isValid);
		}, [isValid, onValidationChange]);

		useEffect(() => {
			if (desiredDetail === renderedDetail) {
				setPendingDetail(null);
				setDetailPanelSettled(false);
				setDetailPanelOpen(desiredDetail !== null);
				return;
			}

			setDetailPanelSettled(false);
			if (desiredDetail === null) {
				setPendingDetail(null);
				setDetailPanelOpen(false);
				return;
			}

			if (renderedDetail === null) {
				setRenderedDetail(desiredDetail);
				setDetailPanelOpen(true);
				return;
			}

			setPendingDetail(desiredDetail);
			setDetailPanelOpen(false);
		}, [desiredDetail, renderedDetail]);

		useEffect(() => {
			return () => {
				clearIndefiniteVisualHoldTimeout();
				if (detailOpenTimeoutRef.current !== null) {
					window.clearTimeout(detailOpenTimeoutRef.current);
				}
			};
		}, []);

		const handleTimeSelect = (value: DurationKey) => {
			clearIndefiniteVisualHoldTimeout();
			setIndefiniteVisualHold(false);
			setTimeWindow(value);

			if (value === 'indefinite') {
				pendingIndefiniteSelectionRef.current = true;
				setCustomModeLocked(false);
				setIndefiniteConfirmed(false);
				return;
			}

			pendingIndefiniteSelectionRef.current = false;
			if (value === 'custom') {
				setCustomModeLocked(true);
				setIndefiniteConfirmed(false);
				onChange(composeLocalDate(customDate, customTime || '12:00'));
				return;
			}

			setCustomModeLocked(false);
			setIndefiniteConfirmed(false);
			onChange(computeExpiryDate(value));
		};

		useImperativeHandle(ref, () => ({
			requestIndefinite: () => handleTimeSelect('indefinite'),
			cancelIndefiniteRequest: () => {
				if (!pendingIndefiniteSelectionRef.current) return;

				pendingIndefiniteSelectionRef.current = false;
				// A confirmation switched on holds the prompt up a moment longer; withdrawn, it goes now.
				clearIndefiniteVisualHoldTimeout();
				setIndefiniteVisualHold(false);
				const key = inferDurationKey(expiresAt, allowIndefinite);
				setCustomModeLocked(key === 'custom');
				setIndefiniteConfirmed(false);
				setTimeWindow(key);
			},
		}));

		const handleCustomDateChange = (value: string) => {
			setCustomDate(value);
			setCustomModeLocked(true);
			if (timeWindow === 'custom' && value) {
				onChange(composeLocalDate(value, customTime));
			}
		};

		const handleCustomTimeChange = (value: string) => {
			setCustomTime(value);
			setCustomModeLocked(true);
			if (timeWindow === 'custom' && customDate) {
				onChange(composeLocalDate(customDate, value));
			}
		};

		const handleIndefiniteConfirmedChange = (value: boolean) => {
			clearIndefiniteVisualHoldTimeout();
			setIndefiniteConfirmed(value);
			if (value) {
				onChange(null);
				setIndefiniteVisualHold(true);
				indefiniteVisualHoldTimeoutRef.current = window.setTimeout(() => {
					indefiniteVisualHoldTimeoutRef.current = null;
					setIndefiniteVisualHold(false);
				}, INDEFINITE_CONFIRMATION_VISUAL_HOLD_MS);
			}
		};

		const getSummaryLabel = () => {
			if (timeWindow === 'custom') {
				if (!customDate) return 'Select date';
				const summaryDate = composeLocalDate(customDate, customTime || '12:00');
				return formatExpirySummary(summaryDate);
			}
			return getExpiryDisplay(timeWindow, customDate, customTime);
		};

		return (
			<VStack gap={0} align="stretch" w="full">
				{showPresets && (
					<DurationPills
						timeWindow={timeWindow}
						onSelect={handleTimeSelect}
						options={moreOptions}
						customLabel={labels.customLabel}
						headerLabel={
							mode === 'edit' ? (
								<HStack gap={2}>
									<Icon as={LuClock} boxSize={5} color="brand.fg" />
									<Title
										level="subsection"
										color="brand.fg"
										css={{
											'@media (max-width: 374px)': { fontSize: 'var(--chakra-font-sizes-md)' },
										}}
									>
										{labels.headerLabel}
									</Title>
								</HStack>
							) : (
								labels.headerLabel
							)
						}
					/>
				)}

				<Box
					{...revealStyles(detailPanelOpen)}
					onTransitionEnd={(event) => {
						if (event.propertyName !== 'grid-template-rows') return;

						if (detailPanelOpen) {
							setDetailPanelSettled(true);
							return;
						}

						if (pendingDetail) {
							setRenderedDetail(pendingDetail);
							setPendingDetail(null);
							detailOpenTimeoutRef.current = window.setTimeout(() => {
								detailOpenTimeoutRef.current = null;
								setDetailPanelOpen(true);
							}, 0);
							return;
						}

						setRenderedDetail(null);
					}}
				>
					<Box minH={0} overflow={renderedDetail === 'custom' && detailPanelSettled ? 'visible' : 'hidden'}>
						{renderedDetail === 'custom' ? (
							<CustomDateTimePicker
								isOpen={showCustom}
								customDate={customDate}
								customTime={customTime}
								onDateChange={handleCustomDateChange}
								onTimeChange={handleCustomTimeChange}
							/>
						) : renderedDetail === 'indefinite' ? (
							<IndefiniteConfirmation
								confirmed={indefiniteConfirmed}
								onConfirmedChange={handleIndefiniteConfirmedChange}
							/>
						) : null}
					</Box>
				</Box>

				{!showIndefinite && <ExpirySummaryBar label={labels.summaryLabel} value={getSummaryLabel()} mt={4} />}
			</VStack>
		);
	},
);

ExpirationDatePicker.displayName = 'ExpirationDatePicker';

export default ExpirationDatePicker;
